import { useState, useEffect, useCallback } from "react";
import type { ModelProvider } from "@/shared/types";

/** One API key per supported provider. Stored in localStorage. */
export type ProviderTokens = Record<ModelProvider, string>;

export interface useAiProviderKeysReturn {
  tokens: ProviderTokens;
  setToken: (provider: ModelProvider, token: string) => void;
  clearToken: (provider: ModelProvider) => void;
  clearAllTokens: () => void;
  hasToken: (provider: ModelProvider) => boolean;
  getToken: (provider: ModelProvider) => string;
}

const STORAGE_KEY = "mcp-inspector-provider-tokens";

const defaultTokens: ProviderTokens = {
  anthropic: "",
  openai: "",
};

function readStoredTokens(): ProviderTokens {
  if (typeof window === "undefined") return defaultTokens;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultTokens;
    // Merge a (possibly legacy/partial) payload onto defaults so missing or
    // malformed fields — or keys for providers that no longer exist — can't
    // leave the state ill-shaped.
    const parsed = JSON.parse(stored) as Partial<Record<string, unknown>>;
    return {
      anthropic:
        typeof parsed.anthropic === "string" ? parsed.anthropic : "",
      openai: typeof parsed.openai === "string" ? parsed.openai : "",
    };
  } catch (error) {
    console.warn("Failed to load provider tokens from localStorage:", error);
    return defaultTokens;
  }
}

export function useAiProviderKeys(): useAiProviderKeysReturn {
  const [tokens, setTokens] = useState<ProviderTokens>(readStoredTokens);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    } catch (error) {
      console.warn("Failed to save provider tokens to localStorage:", error);
    }
  }, [tokens]);

  const setToken = useCallback((provider: ModelProvider, token: string) => {
    setTokens((prev) => ({ ...prev, [provider]: token }));
  }, []);

  const clearToken = useCallback((provider: ModelProvider) => {
    setTokens((prev) => ({ ...prev, [provider]: "" }));
  }, []);

  const clearAllTokens = useCallback(() => {
    setTokens(defaultTokens);
  }, []);

  const hasToken = useCallback(
    (provider: ModelProvider) => Boolean(tokens[provider]?.trim()),
    [tokens]
  );

  const getToken = useCallback(
    (provider: ModelProvider) => tokens[provider] || "",
    [tokens]
  );

  return {
    tokens,
    setToken,
    clearToken,
    clearAllTokens,
    hasToken,
    getToken,
  };
}
